/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ModelIdentifier } from 'src/luis/core/services/luisFeatureService';
import { Intent } from 'src/luis/modules/intents/Intent';
import { UtteranceIntent } from 'src/luis/projects/analytics/types/UtteranceIntent';
import { Entity } from 'src/luis/projects/entities/types/Entity';
import { getEntityStoreOrEntityChildStoreById, isMachineLearned } from 'src/luis/projects/entities/utils/entityHelpers';
import { getChildStoreById } from 'src/luis/projects/entities/utils/simpleEntityHelpers/simpleEntityHelpers';
import { UtterancePredictionsStore } from 'src/luis/projects/utterances/hooks/useUtterances';
import { AddExamplePayloadLabelSwagger } from 'src/luis/projects/utterances/services/mappers';
import { PredictionStore } from 'src/luis/projects/utterances/stores/predictionStore';
import { UtteranceStore } from 'src/luis/projects/utterances/stores/utteranceStore';
import { UtteranceWithAnalytics } from 'src/luis/projects/utterances/types/UtteranceWithAnalytics';
import { Model } from 'src/luis/types/Model';
import { ModelType } from 'src/luis/types/ModelType';
import { LuisId } from 'src/luis/types/Resource';
import { AppVersionStore } from 'src/luis/types/stores/apps/AppVersionStore';
import { CompositeEntityStore } from 'src/luis/types/stores/entities/CompositeEntityStore';
import { EntityRoleStore } from 'src/luis/types/stores/entities/EntityRoleStore';
import { EntityStore } from 'src/luis/types/stores/entities/EntityStore';
import { HierarchicalEntityStore } from 'src/luis/types/stores/entities/HierarchicalEntityStore';
import { SimpleEntityStore } from 'src/luis/types/stores/entities/SimpleEntityStore';
import { EntityLabelStore } from 'src/luis/types/stores/utterances/EntityLabelStore';
import { Label } from 'src/modules/labeling/types/Label';

type TransientExamplePayloadLabelSwagger = AddExamplePayloadLabelSwagger & { entity: EntityStore };

/**
 * Checks for child-parent relation.
 */
const isChildLabel = (parentLabel: TransientExamplePayloadLabelSwagger, childLabel: TransientExamplePayloadLabelSwagger) => {
    const parentEntity = <SimpleEntityStore>parentLabel.entity;
    const parentChildren = <EntityStore[]>parentEntity.children;

    return (
        (parentChildren.find(childEntity => childEntity.id === childLabel.entity.id) ||
            parentChildren.find(childEntity => childLabel.entity.entityRoleStoreHouse.findById(childEntity.id))) &&
        parentLabel.startCharIndex <= childLabel.startCharIndex &&
        parentLabel.endCharIndex >= childLabel.endCharIndex
    );
};

/**
 * Nests the labels swagger array.
 */
const assignLabelsChildren = (labelSwaggers: TransientExamplePayloadLabelSwagger[]): void => {
    const toBeDeletedLabels: number[] = [];
    for (const parentLabel of labelSwaggers) {
        const { entity } = parentLabel;

        /**
         * Hierarchical child entities don't have a stored entity value
         * as their types are not compatible with the EntityStore and
         * it's not needed since we don't no nesting for hierarchical entities anyway.
         */
        if (!entity) {
            continue;
        }

        if (entity.typeId === ModelType.Simple && Boolean((<SimpleEntityStore>entity).children)) {
            for (let j = 0; j < labelSwaggers.length; j++) {
                const label = labelSwaggers[j];

                if (isChildLabel(parentLabel, label)) {
                    parentLabel.children.push({
                        entityName: label.entityName,
                        role: label.role,
                        startCharIndex: label.startCharIndex,
                        endCharIndex: label.endCharIndex,
                        children: label.children
                    });
                    toBeDeletedLabels.push(j);
                }
            }
        }
    }

    toBeDeletedLabels.sort((a, b) => a - b);

    for (let i = toBeDeletedLabels.length - 1; i >= 0; i--) {
        labelSwaggers.splice(toBeDeletedLabels[i], 1);
    }
};

/**
 * Get the entity name, char indices from the entity id and token indices.
 */
const toFlattenedLabelSwagger = ({
    text,
    tokenizedText,
    entityStores
}: Pick<UtteranceStore, 'text' | 'tokenizedText'> & { entityStores: EntityStore[] }) => (
    label: EntityLabelStore
): TransientExamplePayloadLabelSwagger => {
    let entityName: string;
    let roleName: string;
    let entity = entityStores.find(e => e.id === label.entityId);

    if (entity) {
        const role = entity.entityRoleStoreHouse.findById(label.roleId);
        entityName = entity.name;
        roleName = role ? role.name : undefined;
    } else {
        // hierarchical child or n-depth.
        for (const e of entityStores) {
            if (e.typeId === ModelType.Hierarchical) {
                const foundChild = (<HierarchicalEntityStore>e).children.find(child => label.entityId === child.id);
                if (foundChild) {
                    entityName = `${e.name}::${foundChild.name}`;
                    break;
                }
            } else if (e.typeId === ModelType.Simple) {
                const foundChild = (<SimpleEntityStore>e).getChildById(label.entityId);
                if (foundChild) {
                    entityName = foundChild.name;
                    entity = foundChild;
                    break;
                }
            }
        }
    }

    // if the entity got deleted but still has a label.
    if (!entityName) {
        return undefined;
    }

    if (label.endCharIndex && label.startCharIndex) {
        return {
            entity,
            entityName,
            role: roleName,
            endCharIndex: label.endCharIndex,
            startCharIndex: label.startCharIndex,
            children: []
        };
    }

    return { ...label.tokenToCharIndices({ text, tokenizedText }), entity, entityName, role: roleName, children: [] };
};

/**
 * Maps the labels to labels swagger in nested
 * or flattened format depending on the sent
 * nested boolean flag.
 *
 * @param nested a boolean flag to
 * get nested labels swagger object.
 */
export const toLabelsSwagger = (
    {
        text,
        tokenizedText,
        entityStores,
        labels
    }: Pick<UtteranceStore, 'text' | 'tokenizedText'> & {
        entityStores: EntityStore[];
        labels: EntityLabelStore[];
    },
    nested: boolean = false
): AddExamplePayloadLabelSwagger[] => {
    const transformedLabels = labels
        .map(
            toFlattenedLabelSwagger({
                entityStores,
                text,
                tokenizedText
            })
        )
        .filter(Boolean);

    if (nested) {
        assignLabelsChildren(transformedLabels);
    }

    return transformedLabels.map(label => {
        const basicSwaggerObject = {
            entityName: label.entityName,
            role: label.role,
            startCharIndex: label.startCharIndex,
            endCharIndex: label.endCharIndex
        };

        return nested ? { ...basicSwaggerObject, children: label.children } : basicSwaggerObject;
    });
};

/**
 * Takes in an array of labels and the entities of the app, looks for labels that
 * have their entityId set as a role and transforms this into a normal entityId and roleId.
 * This case happens when composite entities have children with roles, in this case the backend
 * sets the child id as the role id, hence the need for this function.
 */
export const resolveLabel = (
    entities: Entity[],
    label: Pick<EntityLabelStore, 'entityId' | 'roleId' | 'startTokenIndex' | 'endTokenIndex'>
) => {
    for (const entity of entities) {
        const foundRole = entity.roles.find(r => r.id === label.entityId);

        if (foundRole) {
            return { start: label.startTokenIndex, end: label.endTokenIndex, entityId: <string>entity.id, roleId: <string>foundRole.id };
        }
    }

    return { entityId: <string>label.entityId, start: label.startTokenIndex, end: label.endTokenIndex, roleId: <string>label.roleId };
};

/**
 * Determines whether the two tokens equals or not.
 */
export const isSameLabel = (
    t1: Pick<EntityLabelStore, 'entityId' | 'startTokenIndex' | 'endTokenIndex' | 'roleId'>,
    t2: Pick<EntityLabelStore, 'entityId' | 'startTokenIndex' | 'endTokenIndex' | 'roleId'>
) =>
    t1.entityId === t2.entityId &&
    t1.startTokenIndex === t2.startTokenIndex &&
    t1.endTokenIndex === t2.endTokenIndex &&
    t1.roleId === t2.roleId;

/**
 * Determines if the predictions valid or not
 *
 * @TODO: Return the ones that need to be refreshed.
 */
export const isInvalidPredictions = (utterances: string[], predictions: Map<string, PredictionStore>): boolean =>
    utterances.some(utterance => (predictions.get(utterance) ? predictions.get(utterance).isDirty : true));

/**
 * Discovers the nearest rival intent to the given utterance.
 * This function has 2 cases:
 * 1. if the analytics of the given utterance is already fetched then it would be easy
 *    to get the nearest rival as it would be the first intent this utterance conflicts
 *    with, here the priority is to the incorrect intents over the unclear ones.
 * 2. the other case is that the analytics is not fetched then the only way to know the
 *    nearest rival is to look at the predictions if the top scoring intent is the labeled
 *    intent then it would be the nearest intent else it would be the second scoring intent.
 *
 * @param utterance The utterance to get the nearest rival intent for.
 */
export const getNearestRivalIntent = (utterance: UtteranceWithAnalytics, intents: Intent[]): UtteranceIntent => {
    if (utterance.analytics?.incorrectIntents.length > 0 || utterance.analytics?.unclearIntents.length > 0) {
        if (utterance.analytics.incorrectIntents.length > 0) {
            return utterance.analytics.incorrectIntents[0];
        }
        if (utterance.analytics.unclearIntents.length > 0) {
            return utterance.analytics.unclearIntents[0];
        }
    }

    const nearestRival = utterance.predictionStore?.predictedIntents.filter(
        pI => pI.intentId !== utterance.utteranceStore.labeledIntentId
    )[0];

    return nearestRival ? { ...nearestRival, intentName: intents.find(intent => intent.id === nearestRival.intentId)?.name } : undefined;
};

/**
 * Gets entity, parent and role ids and resolves to the actual objects.
 */
export const resolveEntityStore = ({
    entityStores,
    parentId,
    entityId,
    roleId
}: {
    entityStores: EntityStore[];
    parentId?: LuisId;
    entityId: LuisId;
    roleId?: LuisId;
}): { entity: EntityStore; parentEntity?: EntityStore; role?: EntityRoleStore } => {
    let entity: EntityStore;
    let parentEntity: EntityStore;
    let role: EntityRoleStore;

    const foundParentEntity = parentId !== entityId ? entityStores.find(e => e.id === parentId) : null;

    if (foundParentEntity && foundParentEntity.typeId === ModelType.Simple) {
        entity = getChildStoreById(entityStores, parentId, entityId);
        parentEntity = foundParentEntity;
    } else if (foundParentEntity && foundParentEntity.typeId === ModelType.Composite) {
        const eId = (<CompositeEntityStore>foundParentEntity).children.find(id => id === entityId);
        entity = entityStores.find(e => e.id === eId);
        parentEntity = foundParentEntity;
    } else if (foundParentEntity && foundParentEntity.typeId === ModelType.Hierarchical) {
        const eId = (<CompositeEntityStore>foundParentEntity).children.find(id => id === entityId);
        entity = entityStores.find(e => e.id === eId);
        parentEntity = foundParentEntity;
    } else {
        entity = entityStores.find(e => e.id === entityId);
    }

    if (!entity) {
        return { entity, parentEntity, role };
    }

    role = entity.entityRoleStoreHouse.findById(roleId);

    return { entity, parentEntity, role };
};

export const getUtteranceStoreHouse = (model: Pick<Model, 'id' | 'typeId'>, appVersionStore: AppVersionStore) => {
    const store =
        model.typeId === ModelType.Intent
            ? appVersionStore.intentStoreHouse.findById(model.id)
            : appVersionStore.entityStoreHouse.findById(model.id);

    return store?.utteranceStoreHouse;
};

/**
 * Returns true if the conditions make add as pattern modal to be disabled
 *
 * @param item The utterance prediction store.
 * @param entities All the available entities for the app.
 */
export const isAddAsPatternDisabled = (items: UtterancePredictionsStore[], entityStores: EntityStore[]): boolean => {
    if (items.length !== 1) {
        return true;
    }
    /**
     * @toDo refactor this part after solving isLoaded entities bug
     */
    if (!entityStores.length) {
        return true;
    }

    if (items[0].utteranceStore.labeledEntities.length > 0) {
        return false;
    }
    if (items[0].predictionStore && items[0].predictionStore.predictedEntities.length > 0) {
        return items[0].predictionStore.predictedEntities.some(e => {
            /**
             * @toDo Makes sure that deleted entities deleted from prediction stores as well.
             */
            const foundEntity = getEntityStoreOrEntityChildStoreById(e.entityId, entityStores);

            return foundEntity ? isMachineLearned(foundEntity.typeId) : false;
        });
    }

    return true;
};

/**
 * Checks if the confirm all predictions command should be disabled or not
 *
 * @param selectedItems The selected utterances.
 * @param entities All the available entities for the app.
 */
export const isConfirmAllPredictionsDisabled = (selectedItems: UtterancePredictionsStore[], entityStores: EntityStore[]): boolean => {
    if (selectedItems.length === 0) {
        return true;
    }
    /**
     * @toDo refactor this part after solving isLoaded entities bug
     */
    if (!entityStores.length) {
        return true;
    }

    for (const item of selectedItems) {
        const foundMLEntities = item.predictionStore?.predictedEntities.some(e => {
            /**
             * @toDo Makes sure that deleted entities deleted from prediction stores as well.
             */
            const foundEntity = getEntityStoreOrEntityChildStoreById(e.entityId, entityStores);

            return foundEntity ? isMachineLearned(foundEntity.typeId) : false;
        });

        if (foundMLEntities) {
            return false;
        }
    }

    return true;
};

/**
 * Gets the utterance stores for the givin model.
 */
export const getUtteranceStores = ({
    model: { id, typeId },
    currentAppVersionStore
}: ModelIdentifier & { currentAppVersionStore: AppVersionStore }) => {
    const { predictionStoreHouse, intentStoreHouse, entityStoreHouse } = currentAppVersionStore;
    const utteranceStoreHouse =
        typeId === ModelType.Intent ? intentStoreHouse.findById(id).utteranceStoreHouse : entityStoreHouse.findById(id).utteranceStoreHouse;

    return { utteranceStoreHouse, predictionStoreHouse };
};

/**
 * Gets the labeling store for the givin model.
 */
export const getLabelingStores = ({ model, currentAppVersionStore }: ModelIdentifier & { currentAppVersionStore: AppVersionStore }) => {
    const { entityStoreHouse, intentStoreHouse, predictionStoreHouse } = currentAppVersionStore;

    const { utteranceStoreHouse } = getUtteranceStores({
        model,
        currentAppVersionStore
    });

    return { entityStoreHouse, utteranceStoreHouse, intentStoreHouse, predictionStoreHouse };
};

/**
 * Determines whether the given label is prediction or not.
 */
export const isMLPrediction = (label: Label) => label.renderedAs === 'box';
